import { API_HOST } from '@/constants/config';

const DEFAULT_LIST_LIMIT = 12;

const toQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

const fetchBlog = async (path, params = {}) => {
  const url = `${API_HOST}${path}${toQueryString(params)}`;
  let response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Platform': 'web',
      },
    });
  } catch (networkErr) {
    throw new Error('Network error — unable to reach the blog service.');
  }

  // Guard against non-JSON responses (e.g. HTML 404 pages)
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      response.ok
        ? 'Blog service returned an unexpected response.'
        : `Blog service returned ${response.status}.`,
    );
  }

  const data = await response.json();
  if (!response.ok || data?.error) {
    throw new Error(data?.message || 'Failed to fetch blog data');
  }

  return data;
};

export const blogApi = {
  listPosts: async ({ page = 1, limit = DEFAULT_LIST_LIMIT, category, tag, q } = {}) => {
    const data = await fetchBlog('/json/blog/posts', { page, limit, category, tag, q });
    return {
      posts: data.posts || [],
      pagination: data.pagination || {},
    };
  },

  searchPosts: async ({ q, page = 1, limit = DEFAULT_LIST_LIMIT, category } = {}) => {
    if (!q?.trim()) {
      return {
        posts: [],
        pagination: { total: 0, page: 1, perPage: limit, pages: 0, hasMore: false },
      };
    }
    const data = await fetchBlog('/json/blog/posts/search', {
      q: q.trim(),
      page,
      limit,
      ...(category ? { category } : {}),
    });
    return {
      posts: data.posts || [],
      pagination: data.pagination || {},
    };
  },

  getPostBySlug: async (slug) => {
    const data = await fetchBlog(`/json/blog/posts/${encodeURIComponent(slug)}`);
    return data.post;
  },

  getCategories: async () => {
    const data = await fetchBlog('/json/blog/categories');
    return data.categories || [];
  },

  getTags: async () => {
    const data = await fetchBlog('/json/blog/tags');
    return data.tags || [];
  },

  getFeaturedPosts: async (limit = 3) => {
    const data = await fetchBlog('/json/blog/featured', { limit });
    return data.posts || [];
  },

  /** Single taxonomy category (public `serialize()` shape) for SEO landing pages. */
  getCategoryBySlug: async (slug) => {
    const s = String(slug || '').trim();
    if (!s) return null;
    try {
      const data = await fetchBlog(`/json/blog/categories/${encodeURIComponent(s)}`);
      return data.category || null;
    } catch {
      return null;
    }
  },
};

